import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Image from "@tiptap/extension-image";
import { Table, TableRow, TableCell, TableHeader } from "@tiptap/extension-table";
import { Bold, Italic, Underline, List, ListOrdered, Quote, Link2, ImagePlus, Table as TableIcon, Undo2, Redo2, AlignLeft, AlignCenter, AlignRight } from "lucide-react";
import { Field, AIButton } from "./AFields";

const isHtml = (v) => typeof v === "string" && /<\w+[^>]*>/.test(v);
export const toHtml = (v) => {
  if (!v) return "";
  if (isHtml(v)) return v;
  return v.split(/\n{2,}|\n/).map((l) => `<p>${l}</p>`).join("");
};

function Btn({ onClick, active, title, children, testid }) {
  return (
    <button type="button" onClick={onClick} title={title} data-testid={testid}
      className={`rounded p-1.5 transition-colors ${active ? "bg-navy text-white" : "text-slate-600 hover:bg-gray-100"}`}>
      {children}
    </button>
  );
}

export function RichTextF({ label, value, onChange, ai, contentType = "article" }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Image,
      Table.configure({ resizable: false }), TableRow, TableCell, TableHeader,
    ],
    content: toHtml(value),
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = toHtml(value);
    if (value !== undefined && next !== current && !editor.isFocused) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [value, editor]); // eslint-disable-line

  if (!editor) return null;
  const c = editor.chain().focus();
  const addLink = () => {
    const url = window.prompt("Link URL (https://…)");
    if (url) editor.chain().focus().setLink({ href: url }).run();
  };
  const addImage = () => {
    const url = window.prompt("Image URL (upload via Media Library, then paste URL)");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  return (
    <Field label={label}>
      <div className="rounded-md border border-gray-200 bg-white focus-within:ring-2 focus-within:ring-saffron-500/50" data-testid={`rte-${label?.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
        <div className="flex flex-wrap items-center gap-0.5 border-b border-gray-100 px-2 py-1.5">
          <Btn onClick={() => c.undo().run()} title="Undo" testid="rte-undo"><Undo2 size={14} /></Btn>
          <Btn onClick={() => c.redo().run()} title="Redo" testid="rte-redo"><Redo2 size={14} /></Btn>
          <span className="mx-1 h-4 w-px bg-gray-200" />
          <Btn onClick={() => c.toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Heading" testid="rte-h2"><span className="text-xs font-black">H2</span></Btn>
          <Btn onClick={() => c.toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Subheading" testid="rte-h3"><span className="text-xs font-black">H3</span></Btn>
          <Btn onClick={() => c.setParagraph().run()} active={editor.isActive("paragraph")} title="Paragraph" testid="rte-p"><span className="text-xs font-black">P</span></Btn>
          <span className="mx-1 h-4 w-px bg-gray-200" />
          <Btn onClick={() => c.toggleBold().run()} active={editor.isActive("bold")} title="Bold" testid="rte-bold"><Bold size={14} /></Btn>
          <Btn onClick={() => c.toggleItalic().run()} active={editor.isActive("italic")} title="Italic" testid="rte-italic"><Italic size={14} /></Btn>
          <Btn onClick={() => c.toggleUnderline().run()} active={editor.isActive("underline")} title="Underline" testid="rte-underline"><Underline size={14} /></Btn>
          <Btn onClick={() => c.toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Quote" testid="rte-quote"><Quote size={14} /></Btn>
          <span className="mx-1 h-4 w-px bg-gray-200" />
          <Btn onClick={() => c.toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bulleted list" testid="rte-ul"><List size={14} /></Btn>
          <Btn onClick={() => c.toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list" testid="rte-ol"><ListOrdered size={14} /></Btn>
          <Btn onClick={addLink} active={editor.isActive("link")} title="Insert link" testid="rte-link"><Link2 size={14} /></Btn>
          <Btn onClick={addImage} title="Insert image" testid="rte-image"><ImagePlus size={14} /></Btn>
          <Btn onClick={() => c.insertTable({ rows: 2, cols: 2, withHeaderRow: true }).run()} title="Insert table" testid="rte-table"><TableIcon size={14} /></Btn>
          <span className="mx-1 h-4 w-px bg-gray-200" />
          <Btn onClick={() => c.setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Align left" testid="rte-left"><AlignLeft size={14} /></Btn>
          <Btn onClick={() => c.setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Align center" testid="rte-center"><AlignCenter size={14} /></Btn>
          <Btn onClick={() => c.setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Align right" testid="rte-right"><AlignRight size={14} /></Btn>
        </div>
        <EditorContent editor={editor} className="prose-ssf min-h-[140px] px-3.5 py-2.5 text-sm [&_.ProseMirror]:min-h-[140px] [&_.ProseMirror]:outline-none [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-gray-200 [&_td]:p-1.5 [&_th]:border [&_th]:border-gray-200 [&_th]:bg-navy-50 [&_th]:p-1.5" />
      </div>
      {ai && (
        <AIButton text={editor.getText()} contentType={contentType} richActions
          onApply={(txt, action = "replace") => {
            if (action === "replace") {
              editor.commands.setContent(toHtml(txt));
            } else if (action === "append") {
              editor.commands.focus("end");
              editor.commands.insertContent(toHtml(txt));
            } else {
              editor.chain().focus().insertContent(toHtml(txt)).run();
            }
            onChange(editor.getHTML());
          }} />
      )}
    </Field>
  );
}
